import { Col, Container, Form, InputGroup, Row } from "react-bootstrap";
import StationPicker from "../../components/Pickers/StationPicker";
import AnalyzeButton from "../../components/AnalyzeButton";
import TimePicker from "../../components/Pickers/TimePicker";
import moment from "moment/min/moment-with-locales";
import { axiosInstance } from "../../auth/Auth";
import { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "react-bootstrap-icons";
import useSortableData from "../../components/useSortableData";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import LoadingIndicator from "../../components/LoadingIndicator";
import { useLoaderData } from "react-router-dom";
import DaypartRow from "../../components/DaypartRow";
import DaysPicker from "../../components/Pickers/DaysPicker";
import { toast } from "react-toastify";
import DaypartHeader from "../../components/DaypartHeader";
import { useUserFormState } from "../../components/UserSelection";

export default function SettingsDayparts() {
    const moduleName = "settingsDayparts";

    const { dayparts } = useLoaderData();

    const [formData, setFormData] = useUserFormState(moduleName);
    const [filterData, setFilterData] = useState({ station: [], name: "" });

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    const tableContainerRef = useRef(null);

    async function handleAddDaypart(event) {
        event.preventDefault();
        const inputs = formData.station.map(x => {
            return {
                radio_id: x.radio_id,
                weekdays: formData.days,
                start_time: moment(formData.startTime).format("HH:mm:00"),
                end_time: moment(formData.endTime).format("HH:mm:59"),
                name: formData.daypartName,
            };
        });
        const promises = inputs.map(x => axiosInstance.post("dayparts/", x));
        const results = await Promise.allSettled(promises);
        const newDayparts = inputs
            .map((x, i) => ({ ...x, daypart_id: results[i].status === "rejected" ? "rejected" : results[i].value.data }))
            .filter(x => x.daypart_id !== "rejected");
        if (newDayparts.length) {
            addDayparts(newDayparts);
            function formatRadioNames(stations) {
                const radioNames = stations.map(station => station.radio_name);
                if (radioNames.length === 2) {
                    return radioNames.join(" and ");
                } else if (radioNames.length > 2) {
                    const lastRadioName = radioNames.pop();
                    return `${radioNames.join(", ")}, and ${lastRadioName}`;
                } else {
                    return radioNames.join(", ");
                }
            }
            let estimationText = "";
            try {
                const response = await axiosInstance.post(
                    "dayparts/estimate",
                    newDayparts.map(x => x.radio_id)
                );
                const minutes = Math.ceil(moment.duration(response.data, "second").asMinutes() + 20 - (moment().minutes() % 20));
                estimationText = `Results will be available in appr. ${minutes} minutes (in Dashboard and Health).`;
            } catch (error) {}
            const radioNamesFormatted = formatRadioNames(formData.station.filter(x => newDayparts.map(d => d.radio_id).includes(x.radio_id)));
            const msg = (
                <span>
                    <strong>{formData.daypartName}</strong> had been added for <strong>{radioNamesFormatted}</strong>
                    <br></br>
                    {estimationText}
                </span>
            );
            toast.success(msg, { autoClose: 15000 });
        } else {
            toast.error(`Error saving daypart. Daypart might already exist on selected stations.`);
        }
    }

    useEffect(() => {
        async function getDayparts() {
            setLoading(true);
            const resolvedDayparts = (await dayparts).data;
            setData(resolvedDayparts);
            setLoading(false);
        }
        getDayparts();
    }, [dayparts]);

    const dataMemo = useMemo(() => {
        if (data) {
            let filteredData = data;
            if (filterData.station.length > 0) {
                filteredData = filteredData.filter(x => filterData.station.map(s => s.radio_id).includes(x.radio_id));
            }
            if (filterData.name) {
                const filters = filterData.name.split(",").filter(x => x.trim());
                filteredData = filteredData.filter(x => x.name && filters.some(f => x.name.toLowerCase().includes(f.toLowerCase().trim())));
            }
            return filteredData;
        }
    }, [data, filterData]);

    const removeDaypart = daypartId => setData(data.filter(x => x.daypart_id !== daypartId));
    const addDayparts = dayparts => setData([...data, ...dayparts]);
    const editDaypart = (daypartId, modifiedDaypart) => setData(data.map(x => (x.daypart_id === daypartId ? modifiedDaypart : x)));

    const { sortedItems, requestSort, sortConfig } = useSortableData(dataMemo, { key: "radio_id", direction: "ascending" });

    const virtualizer = useWindowVirtualizer({
        count: sortedItems?.length || 1,
        estimateSize: () => 40,
        overscan: 50,
        scrollMargin: tableContainerRef.current?.offsetTop ?? 0,
    });

    return (
        <>
            <Row>
                <Col className="bg-white p-4">
                    <h2 className=" text-uppercase">Add Dayparts</h2>
                    <Form onSubmit={handleAddDaypart} className="row gy-3">
                        <Col xs={12} xl>
                            <Row className="gy-3">
                                <Form.Group as={Col} controlId={`${moduleName}-new-name`} className="col-12 col-xl min-w-220">
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.daypartName || ""}
                                        onChange={e => setFormData({ ...formData, daypartName: e.currentTarget.value })}
                                        placeholder="Daypart name"
                                    />
                                </Form.Group>
                                <TimePicker
                                    formData={formData}
                                    setFormData={setFormData}
                                    name={`${moduleName}-new-start`}
                                    label="Start Time"
                                    formKey="startTime"
                                />
                                <TimePicker
                                    formData={formData}
                                    setFormData={setFormData}
                                    name={`${moduleName}-new-end`}
                                    label="End Time (Incl.)"
                                    formKey="endTime"
                                    seconds="59"
                                />
                                <DaysPicker formData={formData} size="md" setFormData={setFormData} name={moduleName} fluid={"sm"} />
                                <StationPicker
                                    formData={formData}
                                    setFormData={setFormData}
                                    name={moduleName}
                                    multiple={true}
                                    label="Add for station(s)"
                                    colXl={4}
                                />
                            </Row>
                        </Col>
                        <Col xs={12} xl={"auto"} className="text-center text-xl-end align-self-end">
                            <AnalyzeButton
                                label="Add new daypart"
                                disabled={
                                    !formData.station.length ||
                                    !formData.daypartName ||
                                    !formData.startTime ||
                                    !formData.endTime ||
                                    !formData.days?.length ||
                                    formData.startTime.getTime() >= formData.endTime.getTime()
                                }
                            />
                        </Col>
                    </Form>
                </Col>
            </Row>
            <hr />
            <Row>
                <Col className="bg-white p-4">
                    <h2 className=" text-uppercase">Dayparts</h2>
                    {loading ? (
                        <span colSpan={12}>
                            <LoadingIndicator width={75} />
                        </span>
                    ) : (
                        data && (
                            <>
                                <Row className="mb-2 gy-2">
                                    <StationPicker
                                        formData={filterData}
                                        setFormData={setFilterData}
                                        name={moduleName}
                                        multiple={true}
                                        placeholder="Search by station"
                                        label={false}
                                        colXl={6}
                                    />
                                    <Form.Group as={Col} className="col-12 col-xl-6">
                                        <InputGroup>
                                            <InputGroup.Text className="input-icon">
                                                <Search />
                                            </InputGroup.Text>
                                            <Form.Control
                                                placeholder="Search by daypart name"
                                                value={filterData.name}
                                                onChange={e => setFilterData({ ...filterData, name: e.target.value })}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Row>
                                <DaypartHeader sortConfig={sortConfig} requestSort={requestSort} />
                                <div ref={tableContainerRef}>
                                    <Container fluid className="position-relative" style={{ height: virtualizer.getTotalSize() }}>
                                        {virtualizer.getVirtualItems().map(virtualRow => {
                                            const item = sortedItems[virtualRow.index];
                                            if (!item) {
                                                return (
                                                    <Row
                                                        key={virtualRow.key}
                                                        data-index={virtualRow.index}
                                                        ref={virtualizer.measureElement}
                                                        style={{ transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)` }}
                                                        className="py-2 border-bottom position-absolute w-100 pe-2">
                                                        <Col className="text-center">No dayparts found</Col>
                                                    </Row>
                                                );
                                            }
                                            return (
                                                <Row
                                                    key={item.daypart_id}
                                                    data-index={virtualRow.index}
                                                    ref={virtualizer.measureElement}
                                                    style={{ transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)` }}
                                                    className="py-1 border-bottom position-absolute w-100">
                                                    <DaypartRow daypart={item} onDelete={removeDaypart} onEdit={editDaypart} />
                                                </Row>
                                            );
                                        })}
                                    </Container>
                                </div>
                            </>
                        )
                    )}
                </Col>
            </Row>
        </>
    );
}
